import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Trash2 } from 'lucide-react';
import { TokenInfo, ClauseInfo, NounChunkInfo, SentenceInfo } from '../../types';
import { isProcessToken, isParticipantToken } from '../../utils/groupingRules';
import { useSoundHandlers } from '../../hooks/useSoundEffects';

interface Step3Props {
  tokens: TokenInfo[];
  clauses: ClauseInfo[];
  nounChunks: NounChunkInfo[];
  sentences: SentenceInfo[];
  onComplete: (score: number) => void;
  isCompleted: boolean;
  showProcessHint?: boolean;
  showParticipantHint?: boolean;
}

// Token 分组结构
interface TokenGroup {
  id: string;
  tokens: TokenInfo[];
  groupType: 'process' | 'participant' | 'other';
  text: string;
  startIndex: number;  // 组内第一个 token 的 index_doc
  endIndex: number;    // 组内最后一个 token 的 index_doc
}

interface Arc {
  id: string;
  fromGroupId: string;
  toGroupId: string;
  type: 'subject-process' | 'process-object';
}

interface GroupPosition {
  id: string;
  left: number;
  top: number;
  width: number;
  height: number;
  centerX: number;
  bottom: number;
}

// 将 tokens 分组：连续的过程/参与者 tokens（同一小句层级）合为一组
function groupTokens(tokens: TokenInfo[]): TokenGroup[] {
  const groups: TokenGroup[] = [];
  let currentGroup: TokenInfo[] = [];
  let currentType: 'process' | 'participant' | 'other' | null = null;
  let currentClsLevel: number | null = null;
  
  const getTokenType = (token: TokenInfo): 'process' | 'participant' | 'other' => {
    if (token.pos === 'PUNCT' || token.pos === 'SPACE') return 'other';
    if (isProcessToken(token, tokens)) return 'process';
    if (isParticipantToken(token, tokens)) return 'participant';
    return 'other';
  };
  
  const pushCurrentGroup = () => {
    if (currentGroup.length > 0 && currentType) {
      groups.push({
        id: `${currentType}-group-${currentGroup[0].index_doc}`,
        tokens: currentGroup,
        groupType: currentType,
        text: currentGroup.map(t => t.text).join(' '),
        startIndex: currentGroup[0].index_doc,
        endIndex: currentGroup[currentGroup.length - 1].index_doc,
      });
    }
    currentGroup = [];
    currentType = null;
    currentClsLevel = null;
  };
  
  tokens.forEach((token, idx) => {
    const tokenType = getTokenType(token);
    const isPunct = token.pos === 'PUNCT' || token.pos === 'SPACE';
    
    if (isPunct) {
      pushCurrentGroup();
      // 标点单独成组
      groups.push({
        id: `punct-${idx}`,
        tokens: [token],
        groupType: 'other',
        text: token.text,
        startIndex: token.index_doc,
        endIndex: token.index_doc,
      });
    } else if (tokenType === currentType && currentType !== 'other' && token.cls_level === currentClsLevel) {
      // 同类型、同层级且是特殊类型，继续累积
      currentGroup.push(token);
    } else {
      // 类型变化或层级变化
      pushCurrentGroup();
      currentGroup = [token];
      currentType = tokenType;
      currentClsLevel = token.cls_level;
    }
  });
  
  // 处理最后的组
  pushCurrentGroup();
  
  return groups;
}

export default function Step3Connection({ 
  tokens, 
  onComplete,
}: Step3Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const groupRefs = useRef<Map<string, HTMLSpanElement>>(new Map());
  const [groupPositions, setGroupPositions] = useState<Map<string, GroupPosition>>(new Map());
  const [arcs, setArcs] = useState<Arc[]>([]);
  const [drawingFrom, setDrawingFrom] = useState<{ groupId: string; type: 'process' | 'participant' } | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
  const [validated, setValidated] = useState(false);
  const { playClick } = useSoundHandlers();

  // 将 tokens 分组
  const tokenGroups = useMemo(() => groupTokens(tokens), [tokens]);

  // 获取过程和参与者组
  const processGroups = useMemo(() => 
    tokenGroups.filter(g => g.groupType === 'process'), 
    [tokenGroups]
  );
  
  const participantGroups = useMemo(() => 
    tokenGroups.filter(g => g.groupType === 'participant'), 
    [tokenGroups]
  );

  // 判断参与者组是主语还是宾语
  const objectGroupIds = useMemo(() => {
    const objectIds = new Set<string>();
    participantGroups.forEach(pGroup => {
      pGroup.tokens.forEach(token => {
        if (token.dep === 'dobj' || token.dep === 'dative' || token.dep === 'attr') {
          objectIds.add(pGroup.id);
        }
      });
    });
    return objectIds;
  }, [participantGroups]);

  // 构建正确的连接关系（基于组）
  const correctConnections = useMemo(() => {
    const connections: { fromGroupId: string; toGroupId: string; type: 'subject-process' | 'process-object' }[] = [];
    
    participantGroups.forEach(pGroup => {
      // 找到参与者组中的任一 token，检查其 head 指向哪个过程组
      pGroup.tokens.forEach(objToken => {
        const headVerb = processGroups.find(vGroup => 
          vGroup.tokens.some(t => t.index_doc === objToken.head_i)
        );
        
        if (headVerb) {
          if (objToken.dep === 'nsubj' || objToken.dep === 'nsubjpass') {
            // 主语 → 过程
            const exists = connections.some(c => 
              c.fromGroupId === pGroup.id && c.toGroupId === headVerb.id && c.type === 'subject-process'
            );
            if (!exists) {
              connections.push({
                fromGroupId: pGroup.id,
                toGroupId: headVerb.id,
                type: 'subject-process'
              });
            }
          } else if (objToken.dep === 'dobj' || objToken.dep === 'dative' || objToken.dep === 'attr') {
            // 过程 → 宾语
            const exists = connections.some(c => 
              c.fromGroupId === headVerb.id && c.toGroupId === pGroup.id && c.type === 'process-object'
            );
            if (!exists) {
              connections.push({
                fromGroupId: headVerb.id,
                toGroupId: pGroup.id,
                type: 'process-object'
              });
            }
          }
        }
      });
    });
    
    return connections;
  }, [processGroups, participantGroups]);

  // 计算组位置
  const updatePositions = useCallback(() => {
    if (!containerRef.current) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const newPositions = new Map<string, GroupPosition>();
    
    groupRefs.current.forEach((el, id) => {
      if (el) {
        const rect = el.getBoundingClientRect();
        newPositions.set(id, {
          id,
          left: rect.left - containerRect.left,
          top: rect.top - containerRect.top,
          width: rect.width,
          height: rect.height,
          centerX: rect.left - containerRect.left + rect.width / 2,
          bottom: rect.top - containerRect.top + rect.height,
        });
      }
    });
    
    setGroupPositions(newPositions);
  }, []);

  useEffect(() => {
    updatePositions();
    window.addEventListener('resize', updatePositions);
    return () => window.removeEventListener('resize', updatePositions);
  }, [updatePositions, tokenGroups]);

  // 处理鼠标移动（画线时）
  useEffect(() => {
    if (!drawingFrom) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    };
    
    const handleMouseUp = () => {
      setDrawingFrom(null);
      setMousePos(null);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [drawingFrom]);

  // 点击组开始画线
  const handleGroupMouseDown = (groupId: string, groupType: 'process' | 'participant') => {
    if (validated) return;
    playClick(); // 播放点击音效
    setDrawingFrom({ groupId, type: groupType });
  };

  // 松开鼠标在组上结束画线
  const handleGroupMouseUp = (groupId: string, groupType: 'process' | 'participant') => {
    if (!drawingFrom || validated) return;
    
    // 验证连接有效性
    let isValid = false;
    let arcType: 'subject-process' | 'process-object' | null = null;
    const fromId = drawingFrom.groupId;
    const toId = groupId;
    
    if (drawingFrom.type === 'participant' && groupType === 'process') {
      // 参与者（蓝）→ 过程（红）= 主语关系
      isValid = true;
      arcType = 'subject-process';
    } else if (drawingFrom.type === 'process' && groupType === 'participant') {
      // 过程（红）→ 参与者（蓝）= 宾语关系
      isValid = true;
      arcType = 'process-object';
    }
    
    if (isValid && arcType && fromId !== toId) {
      // 检查是否已存在相同连接
      const exists = arcs.some(a => 
        a.fromGroupId === fromId && a.toGroupId === toId
      );
      
      if (!exists) {
        setArcs(prev => [...prev, {
          id: `arc-${fromId}-${toId}`,
          fromGroupId: fromId,
          toGroupId: toId,
          type: arcType!,
        }]);
      }
    }
    
    setDrawingFrom(null);
    setMousePos(null);
  };

  // 删除弧线
  const handleDeleteArc = (arcId: string) => {
    setArcs(prev => prev.filter(a => a.id !== arcId));
  };

  // 生成弧线路径（贝塞尔曲线）
  const getArcPath = (from: GroupPosition, to: GroupPosition | { centerX: number; bottom: number }, isDrawing = false) => {
    const startX = from.centerX;
    const startY = from.bottom + 5;
    const endX = isDrawing ? (mousePos?.x || to.centerX) : to.centerX;
    const endY = isDrawing ? (mousePos?.y || to.bottom + 5) : to.bottom + 5;
    
    // 计算弧度（距离越远，弧度越大）
    const distance = Math.abs(endX - startX);
    const curveHeight = Math.min(distance * 0.4, 80) + 30;
    
    const controlY = Math.max(startY, endY) + curveHeight;
    
    return `M ${startX} ${startY} Q ${(startX + endX) / 2} ${controlY} ${endX} ${endY}`;
  };

  // 验证答案
  const handleValidate = () => {
    let correctCount = 0;
    
    arcs.forEach(arc => {
      const isCorrect = correctConnections.some(c => 
        c.fromGroupId === arc.fromGroupId && 
        c.toGroupId === arc.toGroupId && 
        c.type === arc.type
      );
      if (isCorrect) correctCount++;
    });
    
    const score = correctConnections.length > 0 
      ? correctCount / correctConnections.length 
      : 0;
    
    setValidated(true);
    onComplete(score);
  };

  const handleReset = () => {
    setArcs([]);
    setValidated(false);
  };

  // 获取组的文本
  const getGroupText = (groupId: string): string => {
    const group = tokenGroups.find(g => g.id === groupId);
    return group?.text || '';
  };

  return (
    <div>
      {/* 图例 */}
      <div className="flex items-center gap-6 mb-4 text-sm flex-wrap">
        <span className="flex items-center gap-2">
          <span className="w-4 h-4 rounded bg-red-500/30 border border-red-500" />
          <span className="text-gray-400">过程 (Process)</span>
        </span>
        <span className="flex items-center gap-2">
          <span className="w-4 h-4 rounded bg-blue-500/30 border border-blue-500" />
          <span className="text-gray-400">主语 (Subject)</span>
        </span>
        <span className="flex items-center gap-2">
          <span className="w-4 h-4 rounded bg-green-500/30 border border-green-500" />
          <span className="text-gray-400">宾语 (Object)</span>
        </span>
        <span className="flex items-center gap-2">
          <span className="w-6 h-0.5 bg-gradient-to-r from-green-400 to-green-600" />
          <span className="text-gray-400">主语→过程</span>
        </span>
        <span className="flex items-center gap-2">
          <span className="w-6 h-0.5 bg-gradient-to-r from-purple-400 to-purple-600" />
          <span className="text-gray-400">过程→宾语</span>
        </span>
      </div>

      {/* 句子和弧线区域 */}
      <div 
        ref={containerRef}
        className="relative p-6 pb-32 rounded-lg bg-cyber-darker/50 border border-purple-500/10 mb-6 select-none"
      >
        {/* Token 组展示 */}
        <div className="flex flex-wrap gap-x-1 gap-y-2 text-xl leading-loose font-mono items-center">
          {tokenGroups.map((group) => {
            const isPunct = group.tokens[0]?.pos === 'PUNCT' || group.tokens[0]?.pos === 'SPACE';
            const isProcess = group.groupType === 'process';
            const isParticipant = group.groupType === 'participant';
            
            // 标点符号直接渲染
            if (isPunct) {
              return (
                <span key={group.id} className="text-gray-500 mx-0.5">
                  {group.text}
                </span>
              );
            }
            
            // 可连接的组（过程或参与者）
            if (isProcess || isParticipant) {
              const isObject = isParticipant && objectGroupIds.has(group.id);
              const getGroupStyle = () => {
                if (isProcess) {
                  return 'bg-red-500/20 border border-red-500/50 text-red-300 hover:bg-red-500/30 hover:border-red-400';
                }
                if (isObject) {
                  return 'bg-green-500/20 border border-green-500/50 text-green-300 hover:bg-green-500/30 hover:border-green-400';
                }
                return 'bg-blue-500/20 border border-blue-500/50 text-blue-300 hover:bg-blue-500/30 hover:border-blue-400';
              };
              
              return (
                <span
                  key={group.id}
                  ref={(el) => {
                    if (el) groupRefs.current.set(group.id, el);
                  }}
                  onMouseDown={() => handleGroupMouseDown(group.id, isProcess ? 'process' : 'participant')}
                  onMouseUp={() => handleGroupMouseUp(group.id, isProcess ? 'process' : 'participant')}
                  className={`
                    inline-flex items-center gap-1 px-2 py-1 rounded-lg cursor-pointer transition-all duration-200 relative font-semibold
                    ${getGroupStyle()}
                    ${drawingFrom?.groupId === group.id ? 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-cyber-darker' : ''}
                    ${validated ? 'cursor-default' : ''}
                  `}
                >
{group.text}
                </span>
              );
            }
            
            // 其他普通 token
            return (
              <span key={group.id} className="px-1 text-gray-400">
                {group.text}
              </span>
            );
          })}
        </div>

        {/* SVG 弧线层 */}
        <svg 
          className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
          style={{ zIndex: 10 }}
        >
          <defs>
            {/* 主语→过程 箭头 (绿色) */}
            <marker
              id="arrow-subject"
              markerWidth="10"
              markerHeight="10"
              refX="9"
              refY="3"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M0,0 L0,6 L9,3 z" fill="#22c55e" />
            </marker>
            
            {/* 过程→宾语 箭头 (紫色) */}
            <marker
              id="arrow-object"
              markerWidth="10"
              markerHeight="10"
              refX="9"
              refY="3"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M0,0 L0,6 L9,3 z" fill="#a855f7" />
            </marker>
            
            {/* 绘制中箭头 (黄色) */}
            <marker
              id="arrow-drawing"
              markerWidth="10"
              markerHeight="10"
              refX="9"
              refY="3"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M0,0 L0,6 L9,3 z" fill="#facc15" />
            </marker>
          </defs>

          {/* 已画的弧线 */}
          {arcs.map(arc => {
            const fromPos = groupPositions.get(arc.fromGroupId);
            const toPos = groupPositions.get(arc.toGroupId);
            if (!fromPos || !toPos) return null;
            
            const path = getArcPath(fromPos, toPos);
            const isSubjectProcess = arc.type === 'subject-process';
            
            return (
              <g key={arc.id} className="group">
                {/* 弧线阴影 */}
                <path
                  d={path}
                  fill="none"
                  stroke={isSubjectProcess ? 'rgba(34, 197, 94, 0.3)' : 'rgba(168, 85, 247, 0.3)'}
                  strokeWidth="6"
                  strokeLinecap="round"
                />
                {/* 弧线主体 */}
                <path
                  d={path}
                  fill="none"
                  stroke={isSubjectProcess ? '#22c55e' : '#a855f7'}
                  strokeWidth="2"
                  strokeLinecap="round"
                  markerEnd={isSubjectProcess ? 'url(#arrow-subject)' : 'url(#arrow-object)'}
                  className="transition-all duration-200"
                />
              </g>
            );
          })}

          {/* 正在绘制的弧线 */}
          {drawingFrom && mousePos && groupPositions.get(drawingFrom.groupId) && (
            <path
              d={getArcPath(groupPositions.get(drawingFrom.groupId)!, { centerX: mousePos.x, bottom: mousePos.y }, true)}
              fill="none"
              stroke="#facc15"
              strokeWidth="2"
              strokeDasharray="5,5"
              strokeLinecap="round"
              markerEnd="url(#arrow-drawing)"
              className="animate-pulse"
            />
          )}
        </svg>
      </div>

      {/* 已建立的连接列表 */}
      {arcs.length > 0 && (
        <div className="mb-6 p-4 rounded-lg bg-cyber-darker/50 border border-gray-700/50">
          <div className="text-sm text-gray-400 mb-3">已建立的连接 ({arcs.length})：</div>
          <div className="flex flex-wrap gap-2">
            {arcs.map(arc => {
              const fromText = getGroupText(arc.fromGroupId);
              const toText = getGroupText(arc.toGroupId);
              const isSubject = arc.type === 'subject-process';
              
              return (
                <div 
                  key={arc.id}
                  className={`
                    flex items-center gap-2 px-3 py-1.5 rounded-full text-sm
                    ${isSubject 
                      ? 'bg-green-500/10 border border-green-500/30' 
                      : 'bg-purple-500/10 border border-purple-500/30'
                    }
                  `}
                >
                  <span className={isSubject ? 'text-blue-300' : 'text-red-300'}>
                    {fromText}
                  </span>
                  <span className={isSubject ? 'text-green-400' : 'text-purple-400'}>→</span>
                  <span className={isSubject ? 'text-red-300' : 'text-green-300'}>
                    {toText}
                  </span>
                  {!validated && (
                    <button
                      onClick={() => handleDeleteArc(arc.id)}
                      className="ml-1 text-gray-500 hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 操作按钮 */}
      <div className="flex items-center justify-end gap-3">
        {validated ? (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleReset}
            className="cyber-button"
          >
            重新连接
          </motion.button>
        ) : (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleValidate}
            disabled={arcs.length === 0}
            className="cyber-button-primary disabled:opacity-50"
          >
            验证答案
          </motion.button>
        )}
      </div>

      {/* 验证结果 */}
      {validated && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6"
        >
          <div className="flex items-start gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
            <CheckCircle className="w-6 h-6 text-green-400 mt-0.5" />
            <div>
              <span className="text-green-300 font-display">连接练习完成！</span>
              <p className="text-sm text-gray-400 mt-2">正确连接参考：</p>
              <div className="mt-2 space-y-1">
                {correctConnections.map((conn, idx) => {
                  const fromText = getGroupText(conn.fromGroupId);
                  const toText = getGroupText(conn.toGroupId);
                  const isSubject = conn.type === 'subject-process';
                  
                  return (
                    <div key={idx} className="text-sm flex items-center gap-2">
                      <span className={isSubject ? 'text-blue-400' : 'text-red-400'}>
                        {fromText}
                      </span>
                      <span className="text-gray-500">
                        {isSubject ? '(主语)' : '(过程)'}
                      </span>
                      <span className={isSubject ? 'text-green-400' : 'text-purple-400'}>→</span>
                      <span className={isSubject ? 'text-red-400' : 'text-green-400'}>
                        {toText}
                      </span>
                      <span className="text-gray-500">
                        {isSubject ? '(过程)' : '(宾语)'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

